import { CssBaseline, StyledEngineProvider } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { PrimaryPaletteContext } from "./primaryPaletteContext";
import { createAppTheme } from "./theme";
import {
  PrimaryPaletteKey,
  DEFAULT_PRIMARY_PALETTE,
  isPrimaryPaletteKey
} from "./tokens/primaryPalettes";
import {
  COLOR_SCHEME_STORAGE_KEY,
  MODE_STORAGE_KEY,
  PRIMARY_PALETTE_STORAGE_KEY
} from "./storageKeys";

export {
  COLOR_SCHEME_STORAGE_KEY,
  MODE_STORAGE_KEY,
  PRIMARY_PALETTE_STORAGE_KEY,
  THEME_STORAGE_KEYS
} from "./storageKeys";

// Read/write inline rather than via `useLocalStorage`: that hook pulls in
// `@utils/sentry` -> `@utils/toast` -> `BaseButton`/`BaseTypography`, and this
// module is loaded by `setupTests` through `jest.requireActual`, which would
// drag those atoms in unmocked and break every suite that mocks them. MUI
// stores the light/dark mode the same way, so the key sits alongside it.
const readStoredPrimaryPalette = (): PrimaryPaletteKey => {
  try {
    const stored = localStorage.getItem(PRIMARY_PALETTE_STORAGE_KEY);
    // Guarded: a stale or hand-edited entry would otherwise index
    // `PRIMARY_PALETTES` with an unknown key and take the whole app down on
    // boot.
    return isPrimaryPaletteKey(stored) ? stored : DEFAULT_PRIMARY_PALETTE;
  } catch {
    return DEFAULT_PRIMARY_PALETTE;
  }
};

interface Props {
  children: ReactNode;
}

export default function ThemeCustomization({ children }: Props) {
  const [primaryPalette, setPrimaryPaletteState] = useState<PrimaryPaletteKey>(
    readStoredPrimaryPalette
  );

  const setPrimaryPalette = useCallback((next: PrimaryPaletteKey) => {
    setPrimaryPaletteState(next);
    try {
      localStorage.setItem(PRIMARY_PALETTE_STORAGE_KEY, next);
    } catch {
      // Private mode / quota: the palette still applies for this session.
    }
  }, []);

  const theme = useMemo(() => createAppTheme(primaryPalette), [primaryPalette]);

  const primaryPaletteValue = useMemo(
    () => ({ primaryPalette, setPrimaryPalette }),
    [primaryPalette, setPrimaryPalette]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider
        noSsr
        theme={theme}
        defaultMode="light"
        disableTransitionOnChange
        modeStorageKey={MODE_STORAGE_KEY}
        colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      >
        <CssBaseline />
        <PrimaryPaletteContext.Provider value={primaryPaletteValue}>
          {children}
        </PrimaryPaletteContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
